import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { useAuth, isViewingAs, VIEWING_AS_NOTE } from '../context/AuthContext';
import { CheckCircle, AlertTriangle, Users, Shield, Smartphone } from 'lucide-react';
import ConsentFields, { ageFromDob } from '../components/ConsentFields';
import PasswordInput from '../components/PasswordInput';
import SocialSignIn, { PROVIDER_NAME } from '../components/SocialSignIn';
import SocialPendingStep from '../components/SocialPendingStep';
import './AuthPages.css';

// B-14 phase 1: invite links always open in the mobile BROWSER (no deep links yet),
// so after joining we point people at the app for their platform. Testing-round links:
const IOS_APP_URL = 'https://testflight.apple.com/join/19jTj3HT'; // TestFlight
// Play internal-testing opt-in link (testing round). Swap for the public store
// listing URL at GA. Leave '' to hide the button.
const ANDROID_APP_URL = 'https://play.google.com/apps/internaltest/4701304377277308405';

// 'ios' | 'android' | null. Null inside the native app (already installed) and on desktop.
const detectMobileBrowser = () => {
  if (Capacitor.isNativePlatform()) return null;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return null;
};

const ROLE_LABELS = { admin: 'Admin', team_manager: 'Team Manager', coach: 'Coach', player: 'Player', parent: 'Parent / Guardian', supporter: 'Supporter', sponsor: 'Sponsor' };
const TEAM_ROLE_LABELS = { team_manager: 'Team manager', coach: 'Coach', player: 'Player' };

// The list of hats an invite grants — reads the rich v14 payload when present,
// falls back to the single legacy role otherwise.
const inviteRoleLabels = (invite) => {
  const p = invite && invite.payload;
  const rich = p && (p.clubAdmin || (Array.isArray(p.teamRoles) && p.teamRoles.length) || p.guardianIntent);
  if (!rich) return [ROLE_LABELS[invite.role] || invite.role];
  const out = [];
  if (p.clubAdmin) out.push('Club admin');
  (p.teamRoles || []).forEach(tr => out.push(`${TEAM_ROLE_LABELS[tr.role] || tr.role}${tr.teamName ? ` · ${tr.teamName}` : ''}`));
  if (p.guardianIntent) out.push('Parent / Guardian');
  return out;
};

const InviteAcceptPage = () => {
  const { clubSlug, token } = useParams();
  const navigate = useNavigate();
  const { user, login, logout, refreshUser, impersonating, loading: authLoading } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dob, setDob] = useState('');
  const [consents, setConsents] = useState({ analytics: false, marketing: false, audience: false });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // W-18: snapshot of the invite taken at accept time. Logging in clears the
  // query cache (fresh user context), so the success screen must never read
  // the live query — it renders from this snapshot instead.
  const [acceptedInvite, setAcceptedInvite] = useState(null);
  const [guardianFollowup, setGuardianFollowup] = useState(false);
  // W-5: when the quiet post-accept login fails (rate limit, autofill
  // mismatch, anything), stay on the success screen with a way in — don't
  // dump a brand-new member on /login looking like their password was wrong.
  const [autoLoginFailed, setAutoLoginFailed] = useState('');
  // F1-4: existing-account path ("sign in to join") — its own password field.
  const [existingPassword, setExistingPassword] = useState('');
  // A generic (no-email) link where the typed email turns out to already have
  // an account: the server 409s and we switch this page into sign-in mode for
  // that email rather than bouncing them to /login and back.
  const [existingEmail, setExistingEmail] = useState('');
  // B-53: a first-time Apple/Google sign-up waiting for the DOB + terms step,
  // and which provider they joined with (the success screen's app hint).
  const [pendingSocial, setPendingSocial] = useState(null);
  const [joinedVia, setJoinedVia] = useState(null);

  const { data: invite, isLoading, isError } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => axios.get(`/clubs/${clubSlug}/invite/${token}`).then(r => r.data),
    retry: false,
    // W-18: once accepted the token is consumed — a focus-triggered refetch
    // would 4xx and clobber the success/"Get the app" screen with
    // "Invalid or Expired Invite".
    enabled: !success,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (invite?.email) setEmail(invite.email);
    // W-16: bulk-add invites carry the invitee's name — pre-fill, still editable.
    const who = invite?.payload?.invitee;
    if (who) {
      setFirstName(prev => prev || who.firstName || '');
      setLastName(prev => prev || who.lastName || '');
    }
  }, [invite]);

  // B-38 (1): a generic (no-email) link opened while signed in — typically
  // after "Already have an account? Login", which returns here — joins as the
  // signed-in account instead of showing a create-account form they'd have to
  // fill in with their own details. "Not you?" below signs out to create one.
  useEffect(() => {
    if (invite && !invite.email && user?.email && !existingEmail && !success) setExistingEmail(user.email);
  }, [invite, user, existingEmail, success]);

  const handleAccept = async (e) => {
    e.preventDefault();
    setError('');
    const age = ageFromDob(dob);
    if (age === null) { setError('Please enter your date of birth'); return; }
    if (age < 18) { setError('Marakin accounts are for adults aged 18 and over. A parent or guardian can add children and under-18s from their own account.'); return; }
    if (!termsAccepted) { setError('Please accept the Terms of Service to join.'); return; }
    // B-38 (3): same minimum as Register; the server enforces it too.
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setSubmitting(true);
    try {
      // W-5: accepting as a NEW person while someone else is signed in on
      // this browser (Stuart accepted Dave's invite while logged in as Chris)
      // — end that session first so nothing about the accept or the login
      // that follows rides the wrong identity or cache.
      if (user && String(user.email || '').toLowerCase() !== email.trim().toLowerCase()) {
        await logout();
      }
      const res = await axios.post('/invite/accept', { token, email, password, firstName, lastName, dateOfBirth: dob, consents, termsAccepted });
      const needsChild = !!res.data?.guardianIntent;
      // Bulk invites can name the children the club expects this parent to
      // add. Stash them for MyAccountPage's guided add-child flow — localStorage
      // (not router state) so they survive the auto-login redirect AND a later
      // manual visit to the account page from the B-14 mobile success screen.
      const pendingChildren = Array.isArray(res.data?.pendingChildren) ? res.data.pendingChildren : [];
      if (pendingChildren.length) {
        try { localStorage.setItem(`marakin_pending_children:${clubSlug}`, JSON.stringify(pendingChildren)); } catch { /* private mode — flow degrades to manual add */ }
      }
      setGuardianFollowup(needsChild);
      setAcceptedInvite(invite || null); // W-18: freeze before the cache can vanish
      setSuccess(true);
      // WT-10: this accept CREATED the account, so Check your inbox greets
      // it as new, not with the copy for existing accounts.
      const asNew = { newAccount: true };
      if (mobileBrowser) {
        // B-14: on a mobile browser, don't whisk them away — the success screen
        // shows "Get the app" buttons. Log in quietly so "Continue in browser" works.
        login(email, password, asNew).catch(() => {});
      } else {
        // Auto-login, then send guardians to their account page to connect a
        // child; everyone else lands on the club hub.
        setTimeout(async () => {
          try {
            await login(email, password, asNew);
            navigate(needsChild ? `/${clubSlug}/account` : `/${clubSlug}`);
          } catch (loginErr) {
            // W-5: account + membership exist; only the sign-in hiccuped.
            setAutoLoginFailed(loginErr?.response?.data?.error || 'We couldn\'t sign you in automatically.');
          }
        }, needsChild ? 3000 : 2000);
      }
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.code === 'account_exists') {
        setExistingEmail(email.trim());
        setError('');
      } else {
        setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      }
    } finally { setSubmitting(false); }
  };

  // F1-4: the invited email already has an account → sign in (or use the
  // current session) and join. Login auto-accepts pending email-specific
  // invites server-side, so a 404/410 from /accept afterwards just means
  // "already applied" — check the membership rather than the status code.
  const handleExistingJoin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const inviteEmail = (invite?.email || existingEmail || email).trim();
    try {
      let me = user;
      const alreadyMe = !!(user && String(user.email || '').toLowerCase() === inviteEmail.toLowerCase());
      if (!alreadyMe) {
        if (user) await logout();
        me = await login(inviteEmail, existingPassword);
      }
      await joinAs(me, inviteEmail);
    } catch (err) {
      setError(err.response?.data?.error || 'Sign-in failed. Check your password and try again.');
    } finally { setSubmitting(false); }
  };

  // Signed in as `me` (password or, B-53, Apple/Google): join with this
  // invite unless sign-in already did (login's B-59 auto-accept). Throws on
  // an /invite/accept refusal; callers word the error.
  const joinAs = async (me, inviteEmail) => {
    // B-79: for a member invite, the club's sponsor login isn't a membership
    // (the server refuses it, 409 sponsor_account, and the catch shows why);
    // for a sponsor invite, only the sponsor login is. (The invite reply has
    // no `kind`; a UC-B3 sponsor invite is the one with role 'sponsor'.)
    const sponsorInvite = invite?.kind === 'sponsor' || invite?.role === 'sponsor';
    const hasMembership = (u) => Array.isArray(u?.memberships) && u.memberships.some(m => m.clubSlug === clubSlug && !m.implicit
      && (sponsorInvite ? m.role === 'sponsor' : m.role !== 'sponsor'));
    // Guardian follow-up comes from the invite SNAPSHOT, not only the accept
    // response — login's auto-accept applies email invites server-side and
    // returns nothing about children, and bulk-invited existing parents are
    // exactly who W-16 names on invites.
    const snapshotChildren = Array.isArray(invite?.payload?.children) ? invite.payload.children.filter(c => c && c.firstName) : [];
    let needsChild = !!invite?.payload?.guardianIntent || snapshotChildren.length > 0;
    let pendingChildren = snapshotChildren;
    if (!hasMembership(me)) {
      try {
        const res = await axios.post('/invite/accept', { token, email: inviteEmail });
        needsChild = needsChild || !!res.data?.guardianIntent;
        if (Array.isArray(res.data?.pendingChildren) && res.data.pendingChildren.length) pendingChildren = res.data.pendingChildren;
      } catch (acceptErr) {
        const st = acceptErr?.response?.status;
        if (st !== 404 && st !== 410) throw acceptErr;
        // 404/410 = the invite was consumed — by login's auto-accept, or
        // revoked/expired since the page loaded. Only the former is success.
        const fresh = await refreshUser();
        if (!hasMembership(fresh)) {
          setError('This invite is no longer valid — ask your club admin to send a new one.');
          return;
        }
      }
    }
    if (pendingChildren.length) {
      try { localStorage.setItem(`marakin_pending_children:${clubSlug}`, JSON.stringify(pendingChildren)); } catch { /* private mode */ }
    }
    setGuardianFollowup(needsChild);
    setAcceptedInvite(invite || null);
    setSuccess(true);
    if (!mobileBrowser) {
      setTimeout(() => navigate(needsChild ? `/${clubSlug}/account` : `/${clubSlug}`), needsChild ? 3000 : 1500);
    }
  };

  // B-53: signed in with Apple/Google (an existing account, or a new one
  // after the DOB + terms step). If the provider confirmed the invited email,
  // sign-in has already applied the invite (B-59); joinAs sees the membership.
  // A generic link joins as the signed-in account, like "sign in to join".
  // An invite for a DIFFERENT address can't be used by this account: say so
  // and sign out again, rather than leave them signed in as someone the page
  // isn't about.
  const handleSocialSignedIn = async (me, provider) => {
    setError('');
    const meEmail = String(me?.email || '').toLowerCase();
    if (invite?.email && meEmail !== String(invite.email).toLowerCase()) {
      await logout();
      setPendingSocial(null);
      setError(`That ${PROVIDER_NAME[provider] || ''} account uses ${me?.email || 'a different email'}, but this invite was sent to ${invite.email}. Use the account with that address, or join with your email and password.`);
      return;
    }
    setSubmitting(true);
    try {
      setJoinedVia(provider);
      await joinAs(me, me.email);
    } catch (err) {
      setJoinedVia(null);
      setError(err.response?.data?.error || 'We couldn\'t add you to the club. Please try again.');
    } finally { setSubmitting(false); }
  };

  const accentColor = (acceptedInvite || invite)?.accent_color || '#1E4A35';
  const mobileBrowser = detectMobileBrowser();
  const appUrl = mobileBrowser === 'ios' ? IOS_APP_URL : mobileBrowser === 'android' ? ANDROID_APP_URL : '';

  // WT-1 review: and while a stored session loads, so a signed-in invitee
  // isn't shown the register form first.
  if (isLoading || authLoading) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>
          <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (isError && !success) { // W-18: never let a late error out-rank the success screen
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <AlertTriangle size={40} style={{ color: 'var(--danger)', marginBottom: 16 }} />
          <h2>Invalid or Expired Invite</h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: 8 }}>This invite link is no longer valid. Ask your club admin to send a new one.</p>
          <Link to="/" className="btn btn-primary" style={{ marginTop: 20 }}>Go to Marakin</Link>
        </div>
      </div>
    );
  }

  if (success) {
    const inv = acceptedInvite || invite || {}; // W-18: survive a cleared cache
    const roleLabels = inviteRoleLabels(inv).filter(Boolean);
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <CheckCircle size={48} style={{ color: 'var(--success)', marginBottom: 16 }} />
          <h2>Welcome to {inv.club_name || 'your club'}!</h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: 8 }}>
            {roleLabels.length > 0 && <>You've joined as {roleLabels.join(', ')}.</>}
            {!mobileBrowser && !autoLoginFailed && (guardianFollowup
              ? ' Next, connect your child to the club from your account page — taking you there now…'
              : ' Redirecting to the club hub…')}
          </p>
          {autoLoginFailed && (
            <div style={{ marginTop: 16, textAlign: 'left', background: 'var(--surface-2, #F4EFE6)', borderRadius: 12, padding: '14px 16px' }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                Your account is ready and you're a member of {inv.club_name || 'the club'} — we just couldn't sign you in automatically ({autoLoginFailed}).
              </p>
              <Link to="/login" state={{ from: guardianFollowup ? `/${clubSlug}/account` : `/${clubSlug}`, email }}
                className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 12, background: accentColor, textDecoration: 'none' }}>
                Sign in as {email}
              </Link>
            </div>
          )}

          {/* B-14 phase 1: mobile browser → point at the app for this platform */}
          {mobileBrowser && (
            <div style={{ marginTop: 20, textAlign: 'left', background: 'var(--surface-2, #F4EFE6)', borderRadius: 12, padding: '16px 18px' }}>
              <p style={{ margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Smartphone size={16} /> Marakin is best in the app
              </p>
              <p style={{ margin: '8px 0 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
                {joinedVia
                  // B-53: no password was made. Apple isn't offered in the
                  // Android app, so an Apple joiner there sets a password.
                  ? (joinedVia === 'apple' && mobileBrowser === 'android'
                    ? 'In the app, tap "Forgot password?" with your Apple email to set a password, then sign in with it.'
                    : `${appUrl ? 'After installing, open the app and' : 'If you have the Marakin app installed, open it and'} choose "Continue with ${PROVIDER_NAME[joinedVia]}".`)
                  : (appUrl
                    ? 'After installing, open the app and log in with the email and password you just created.'
                    : 'If you have the Marakin app installed, open it and log in with the email and password you just created.')}
              </p>
              {appUrl && (
                <a href={appUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center', background: accentColor, textDecoration: 'none' }}>
                  {mobileBrowser === 'ios' ? 'Get the app (TestFlight)' : 'Get the app on Google Play'}
                </a>
              )}
              <button type="button" className="btn"
                onClick={() => navigate(guardianFollowup ? `/${clubSlug}/account` : `/${clubSlug}`)}
                style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}>
                Continue in browser{guardianFollowup ? ' — connect your child' : ''}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // B-65 review: a staff "view as" session can't accept invites (the server
  // refuses it). Say so instead of offering buttons that would fail.
  if (user && isViewingAs(impersonating)) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1>Join {invite?.club_name || 'this club'}</h1>
          <p className="auth-subtitle">{VIEWING_AS_NOTE}</p>
        </div>
      </div>
    );
  }

  if (pendingSocial) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <SocialPendingStep
            pending={pendingSocial}
            title={`Join ${invite.club_name}`}
            onDone={(me) => { setPendingSocial(null); return handleSocialSignedIn(me, pendingSocial.provider); }}
            onCancel={() => setPendingSocial(null)}
          />
        </div>
      </div>
    );
  }

  // B-53: shown above both forms below, unless already signed in as the
  // invitee (then there is nothing to sign in to).
  const signedInAsInvitee = !!(user && String(user.email || '').toLowerCase() === String(invite.email || existingEmail || '').toLowerCase() && (invite.email || existingEmail));
  const socialButtons = !signedInAsInvitee && (
    <SocialSignIn
      onSignedIn={handleSocialSignedIn}
      onNeedsProfile={(p) => {
        // Don't create an account this invite can't use (different address).
        if (invite.email && String(p.email || '').toLowerCase() !== String(invite.email).toLowerCase()) {
          setError(`That ${PROVIDER_NAME[p.provider] || ''} account uses ${p.email || 'a different email'}, but this invite was sent to ${invite.email}. Use the account with that address, or join with your email and password.`);
          return;
        }
        setError('');
        setPendingSocial(p);
      }}
      dividerText={(invite.emailExists || existingEmail) ? 'or use your password' : 'or create a password'}
    />
  );

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {invite.logo_url && (
            <img src={invite.logo_url} alt={invite.club_name} style={{ width: 64, height: 64, borderRadius: 16, objectFit: 'cover', marginBottom: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }} />
          )}
          <h2 style={{ margin: 0 }}>Join {invite.club_name}</h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {inviteRoleLabels(invite).map((label, i) => (
              <span key={i} className="badge badge-green"><Shield size={12} /> {label}</span>
            ))}
          </div>
          <p style={{ color: 'var(--text-secondary)', marginTop: 10, fontSize: 14 }}>
            {(invite.emailExists || existingEmail)
              ? (user && String(user.email || '').toLowerCase() === String(invite.email || existingEmail).toLowerCase()
                ? <>Join {invite.club_name} with your Marakin account.</>
                : <>This email already has a Marakin account — sign in to join {invite.club_name}.</>)
              : <>You've been invited to join {invite.club_name}. Create your account to get started.</>}
          </p>
        </div>

        {/* F1-4: existing account → sign in to join (no dead password field). */}
        {socialButtons}
        {(invite.emailExists || existingEmail) ? (
          <form onSubmit={handleExistingJoin}>
            <div className="form-group">
              <label className="label">Email</label>
              <input className="input" type="email" value={invite.email || existingEmail} readOnly
                style={{ background: 'var(--surface-2)', color: 'var(--text-secondary)' }} />
            </div>
            {user && String(user.email || '').toLowerCase() === String(invite.email || existingEmail).toLowerCase() ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 12 }}>
                You're signed in as {user.firstName} {user.lastName}.
                {!invite.email && (
                  <> Not you? <button type="button" className="btn-link"
                    onClick={async () => { await logout(); setExistingEmail(''); setError(''); }}
                    style={{ background: 'none', border: 'none', padding: 0, color: accentColor, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                    Sign out and create a new account</button></>
                )}
              </p>
            ) : (
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="label">Password</label>
                <PasswordInput required value={existingPassword}
                  onChange={e => setExistingPassword(e.target.value)}
                  placeholder="Your Marakin password" />
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  Forgotten it? <Link to="/forgot-password" style={{ color: accentColor, fontWeight: 700 }}>Reset your password</Link>, then come back to this link.
                </p>
              </div>
            )}
            {error && (
              <div className="invite-error" style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(185,64,64,0.06)', border: '1px solid rgba(185,64,64,0.15)', borderRadius: 'var(--radius-sm)', color: 'var(--danger)', fontSize: 13 }}>
                <AlertTriangle size={14} /> {error}
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={submitting}
              style={{ width: '100%', justifyContent: 'center', marginTop: 20, background: accentColor }}>
              {submitting ? 'Joining...' : <><Users size={15} /> Sign in and join {invite.club_name}</>}
            </button>
          </form>
        ) : (
        <form onSubmit={handleAccept}>
          <div className="auth-form-row">
            <div className="form-group">
              <label className="label">First name</label>
              <input className="input" required value={firstName} onChange={e => setFirstName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="label">Last name</label>
              <input className="input" required value={lastName} onChange={e => setLastName(e.target.value)} />
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="label">Email</label>
            <input className="input" type="email" required value={email}
              onChange={e => setEmail(e.target.value)}
              readOnly={!!invite.email}
              style={invite.email ? { background: 'var(--surface-2)', color: 'var(--text-secondary)' } : {}} />
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="label">Password</label>
            <PasswordInput required minLength={8} value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Minimum 8 characters" />
          </div>

          <div style={{ marginTop: 12 }}>
            <ConsentFields
              dob={dob}
              onDobChange={e => setDob(e.target.value)}
              consents={consents}
              onConsentsChange={setConsents}
              termsAccepted={termsAccepted}
              onTermsChange={setTermsAccepted}
            />
          </div>

          {error && (
            <div className="invite-error" style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(185,64,64,0.06)', border: '1px solid rgba(185,64,64,0.15)', borderRadius: 'var(--radius-sm)', color: 'var(--danger)', fontSize: 13 }}>
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            style={{ width: '100%', justifyContent: 'center', marginTop: 20, background: accentColor }}
          >
            {submitting ? 'Joining...' : <><Users size={15} /> Join {invite.club_name}</>}
          </button>
        </form>
        )}

        {!invite.emailExists && !existingEmail && (
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', marginTop: 16 }}>
            Already have an account? <Link to="/login" state={{ from: `/${clubSlug}/invite/${token}` }} style={{ color: accentColor, fontWeight: 700 }}>Login</Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default InviteAcceptPage;
